import React, { FC, useContext, useState, ReactElement } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Notification } from "@app/components";
import "./billingTestCard.scss";
import { Checkbox } from "@natera/form";
import { IntlContext } from "@app/provider";
import { getFormattedLocalizeDate } from "@app/utils";
import StatusChip from "../statusChip";
import {
  PaymentDueInfo,
  PaymentDueStatus,
  StatementStatus,
  TestName,
} from "@app/postBill/models";
import { convertBalance } from "@app/postBill/utils/convertBalance";
import { getLocalizedTestFullName } from "@app/postBill/utils/testNameDisplay";
import BalanceProcessingDialog from "../balanceProcessingDialog";
import { useDialog } from "@natera/platform/lib/hooks";
import MagnifierIcon from "@app/postBill/assets/svg/magnifier-icon.svg";
import Svg from "@natera/material/lib/svg";

const messages = defineMessages({
  billingTestCardViewStatement: {
    id: "billingTestCardViewStatement",
    defaultMessage: "View statement",
  },
  billingTestCardDueDate: {
    id: "billingTestCardDueDate",
    defaultMessage: "Due date",
  },
  billingTestCardExpirationDate: {
    id: "billingTestCardExpirationDate",
    defaultMessage: "Expiration date",
  },
  billingTestCardCaseNumber: {
    id: "billingTestCardCaseNumber",
    defaultMessage: "Case number",
  },
  billingTestCardDateOfService: {
    id: "billingTestCardDateOfService",
    defaultMessage: "Date of service",
  },
  billingTestCardOrderedBy: {
    id: "billingTestCardOrderedBy",
    defaultMessage: "Ordered by",
  },
  billingTestCardPendingInfo: {
    id: "billingTestCardPendingInfo",
    defaultMessage:
      "A portion of your balance is still processing. We’ll notify you once the remaining amount is confirmed.",
  },
  billingTestCardPendingLearnMore: {
    id: "billingTestCardPendingLearnMore",
    defaultMessage: "Learn more",
  },
  billingTestCardDueUponReceipt: {
    id: "billingTestCardDueUponReceipt",
    defaultMessage: "Due upon receipt",
  },
  billingTestCardDueNow: {
    id: "billingTestCardDueNow",
    defaultMessage: "Due now",
  },
  billingTestCardPaymentReceivedOn: {
    id: "billingTestCardPaymentReceivedOn",
    defaultMessage: "Payment received on",
  },
});

interface BillingTestCardProps {
  testName: TestName;
  amount: number;
  dueDate: PaymentDueInfo;
  caseNumber: string;
  dateOfService: string;
  orderedBy: string;
  statementStatus: StatementStatus;
  selectable?: boolean;
  selected?: boolean;
  ups?: boolean;
  hasPdf?: boolean;
  currency?: string;
  onViewStatement?: () => void;
  onCheckboxClick?: () => void;
}

const BillingTestCard: FC<BillingTestCardProps> = ({
  testName,
  amount,
  dueDate,
  caseNumber,
  dateOfService,
  orderedBy,
  statementStatus,
  selectable = false,
  selected = false,
  ups = false,
  hasPdf = false,
  currency,
  onViewStatement = () => undefined,
  onCheckboxClick = () => undefined,
}) => {
  const intl = useIntl();
  const { currentLanguage } = useContext(IntlContext);
  const [checkboxValue, setCheckboxValue] = useState(selected);
  const balanceProcessingModal = useDialog(BalanceProcessingDialog);

  const handleSetCheckboxValue = () => {
    setCheckboxValue((oldCheckboxValue) => !oldCheckboxValue);
    onCheckboxClick();
  };

  const closeBlanceProcessingDialog = () => {
    balanceProcessingModal.close();
  };

  const openBlanceProcessingDialog = () => {
    balanceProcessingModal.open({
      onCloseDialog: () => closeBlanceProcessingDialog(),
    });
  };

  const pendingInfo: ReactElement = (
    <Notification type="note">
      <div className="billing-test-card__note">
        {intl.formatMessage(messages.billingTestCardPendingInfo)}
        <span onClick={openBlanceProcessingDialog}>
          {intl.formatMessage(messages.billingTestCardPendingLearnMore)}
        </span>
      </div>
    </Notification>
  );

  const renderDueDateRow = (
    dueDateObj: PaymentDueInfo,
    isPaymentPending: boolean
  ) => {
    if (!dueDateObj || dueDateObj.pastDueExists === PaymentDueStatus.EMPTY) {
      return null;
    }

    let value: string;

    switch (dueDateObj.pastDueExists) {
      case PaymentDueStatus.DUE_NOW:
        value = intl.formatMessage(messages.billingTestCardDueNow);
        break;
      case PaymentDueStatus.DUE_UPON_RECEIPT:
        value = intl.formatMessage(messages.billingTestCardDueUponReceipt);
        break;
      case PaymentDueStatus.VALID_DATE:
        value = getFormattedLocalizeDate(dueDateObj.dueDate, currentLanguage);
        break;
      default:
        return null;
    }

    let dateLabel = "";
    if (ups) {
      dateLabel = intl.formatMessage(messages.billingTestCardExpirationDate);
    } else if (isPaymentPending) {
      dateLabel = intl.formatMessage(messages.billingTestCardPaymentReceivedOn);
    } else {
      dateLabel = intl.formatMessage(messages.billingTestCardDueDate);
    }

    return (
      <div className="billing-test-card__row">
        <span className="billing-test-card__label">{dateLabel}</span>
        <span className="billing-test-card__value">{value}</span>
      </div>
    );
  };

  return (
    <>
      {balanceProcessingModal.getDialog()}
      <div className="billing-test-card" data-testid="billingTestCard">
        <div className="billing-test-card__display">
          {selectable && (
            <div className="billing-test-card__display__checkbox">
              <Checkbox
                checked={checkboxValue}
                onChange={handleSetCheckboxValue}
                data-testid="billingTestCardCheckbox"
              />
            </div>
          )}
          <div className="billing-test-card__display__content">
            <div className="billing-test-card__header">
              <div className="billing-test-card__title">
                {getLocalizedTestFullName(testName, intl)}
              </div>
              <div className="billing-test-card__amount">
                {statementStatus === StatementStatus.CLAIM_PENDING
                  ? convertBalance(0, true, currency)
                  : convertBalance(amount, false, currency)}
              </div>
            </div>
            {statementStatus && (
              <div className="billing-test-card__status">
                <StatusChip status={statementStatus} />
              </div>
            )}
            <div className="billing-test-card__content">
              {renderDueDateRow(
                dueDate,
                statementStatus === StatementStatus.PAYMENT_PENDING
              )}
              <div className="billing-test-card__row">
                <span className="billing-test-card__label">
                  {intl.formatMessage(messages.billingTestCardCaseNumber)}
                </span>
                <span className="billing-test-card__value">{caseNumber}</span>
              </div>
              <div className="billing-test-card__row">
                <span className="billing-test-card__label">
                  {intl.formatMessage(messages.billingTestCardDateOfService)}
                </span>
                <span className="billing-test-card__value">
                  {getFormattedLocalizeDate(dateOfService, currentLanguage)}
                </span>
              </div>
              <div className="billing-test-card__row">
                <span className="billing-test-card__label">
                  {intl.formatMessage(messages.billingTestCardOrderedBy)}
                </span>
                <span className="billing-test-card__value">{orderedBy}</span>
              </div>
              {statementStatus &&
                (statementStatus == StatementStatus.CLAIM_PENDING ||
                  statementStatus == StatementStatus.PARTIAL_PENDING) &&
                pendingInfo}
            </div>
            {!ups && hasPdf && (
              <button
                className="billing-test-card__button"
                onClick={onViewStatement}
              >
                <Svg
                  content={MagnifierIcon}
                  onClick={onViewStatement}
                  className="billing-test-card__button-icon"
                />
                {intl.formatMessage(messages.billingTestCardViewStatement)}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingTestCard;
